/**
 * SIME period helpers.
 * Periods run from the 20th of one month to the 20th of the next.
 */

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Map a JS Date to its SIME period (YYYY-MM).
 * Periods run from the 21st to the 20th: "June" = May 21 → June 20.
 * Day >= 21 → next month's period. Day 1-20 → this month's period.
 */
function dateObjectToPeriodo(d: Date): string {
  let year = d.getFullYear();
  let month = d.getMonth(); // 0-indexed
  if (d.getDate() >= 21) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

/**
 * Current SIME period (21-to-20 convention).
 * If today >= 21st → next month's YYYY-MM.
 * If today <= 20th → this month's YYYY-MM.
 */
export function getCurrentPeriod(): string {
  return dateObjectToPeriodo(new Date());
}

/**
 * Convert an ISO date (YYYY-MM-DD) to its SIME period (YYYY-MM).
 * Period spans day 20 of one month to day 20 of the next.
 *   day >= 20 → current month
 *   day < 20  → previous month
 *
 * Returns empty string for invalid dates so callers can distinguish "no input"
 * from a real period value.
 */
export function dateToPeriodo(isoDate: string): string {
  if (!isoDate) return '';
  // Parse as local date (YYYY-MM-DD is treated as UTC by Date constructor,
  // which shifts the day in negative timezones). Split-and-construct avoids that.
  const parts = isoDate.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const day = parts[2];
  if (!y || !m || !day) return '';
  const d = new Date(y, m - 1, day);
  if (Number.isNaN(d.getTime())) return '';
  return dateObjectToPeriodo(d);
}

/**
 * Generate period options (12 months back, 3 months forward).
 * Each period is YYYY-MM with a display label like "Mar 2026".
 *
 * Ordered NEWEST-first (descending): the dropdown opens on the most recent
 * periods, not the oldest. Iterating offset from +3 down to -12 means the
 * list starts near the current period instead of "starting from 2025".
 */
export function generatePeriodOptions(): { value: string; label: string }[] {
  const now = new Date();
  let baseMonth = now.getMonth(); // 0-indexed
  let baseYear = now.getFullYear();
  if (now.getDate() >= 21) {
    baseMonth += 1;
    if (baseMonth > 11) { baseMonth = 0; baseYear += 1; }
  }
  const options: { value: string; label: string }[] = [];
  for (let offset = 3; offset >= -12; offset--) {
    const d = new Date(baseYear, baseMonth + offset, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ value, label });
  }
  return options;
}
